import { PlayArrow } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import LineWeightIcon from '@mui/icons-material/LineWeight';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PartitionWallDrawerProps } from '../../../@types/ContextProviderRelated';
import { EUnitType, EWallInput } from '../../../@types/Enums';
import { useAttributeActions } from '../../../hooks/useAttributeActions';
import AttributeModel, { IAttribute } from '../../../models/standard/AttributeModel';
import PartitionWallModel, { IPartitionWall } from '../../../models/standard/PartitionWallModel';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { usePlan } from '../../../provider/PlanProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { deleteButton, immersightBlue } from '../../../utils/colors';
import {
  MAX_WALL_DEPTH,
  MAX_WALL_DEPTH_IMPERIAL,
  MAX_WALL_LENGTH,
  MAX_WALL_LENGTH_IMPERIAL,
  MIN_WALL_DEPTH,
  MIN_WALL_DEPTH_IMPERIAL,
  MIN_WALL_LENGTH,
  MIN_WALL_LENGTH_IMPERIAL,
  UNIT_TO_METER_DIVISOR
} from '../../../utils/constants';
import {
  cmToInternalRounded,
  getLocalizedUnitLabel,
  inchToInternalRounded,
  internalToCentimetersRounded,
  internalToInchesRounded
} from '../../../utils/conversionUtils';
import filesApiService from '../../../utils/filesApiService';
import { handleWheelInput, WheelInputConfig } from '../../../utils/handleMouseEvents';
import AttributeAutocomplete from '../AttributeAutocomplete';
import AttributeList from '../AttributeList';

const PartitionWallDrawer = ({
  open,
  onClose,
  wallIndex,
  resetSelection,
  id,
}: PartitionWallDrawerProps): JSX.Element | null => {
  const {
    partitionWalls,
    updatePartitionWallThickness,
    updatePartitionWallLength,
    deletePartitionWall,
    updatePartitionWallAttributes,
  } = useBuilding();

  const { unitType } = useUnitContext();
  const intl = useIntl();
  const cmLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'centimeter'), [intl]);
  const inLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'inch'), [intl]);

  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  // 1) Capture a stable wall uuid from the incoming index (might be stale later)
  const wallUuid = useMemo(() => partitionWalls?.[wallIndex]?.uuid, [partitionWalls, wallIndex]);

  // 2) Resolve wall + safeIndex by uuid (never trust the old index)
  const safeIndex = useMemo(() => {
    if (!wallUuid) return -1;
    return partitionWalls.findIndex((w) => w?.uuid === wallUuid);
  }, [partitionWalls, wallUuid]);

  const wall = useMemo<IPartitionWall | undefined>(() => {
    if (safeIndex < 0) return undefined;
    return partitionWalls[safeIndex] as IPartitionWall | undefined;
  }, [partitionWalls, safeIndex]);

  // Close drawer automatically if selection becomes invalid
  useEffect(() => {
    if (!open) return;
    if (!wallUuid || safeIndex < 0 || !wall) {
      resetSelection();
      onClose();
    }
  }, [open, wallUuid, safeIndex, wall, resetSelection, onClose]);

  // ----------------------------
  // Attributes UI State
  // ----------------------------
  const [attributes, setAttributes] = useState<AttributeModel[]>([]);
  const [attributeValues, setAttributeValues] = useState<Record<number, string>>({});
  const [showInputFields, setShowInputFields] = useState<Record<number, boolean>>({});

  const [allAttributes, setAllAttributes] = useState<IAttribute[]>([]);
  const [attrSelected, setAttrSelected] = useState<IAttribute | null>(null);
  const [attrInputValue, setAttrInputValue] = useState('');
  const [attrError, setAttrError] = useState<string | null>(null);

  // Safe updater used by attribute actions hook
  const pushAttributesToStore = useCallback(
    (attrs: AttributeModel[]) => {
      if (safeIndex < 0) return;
      updatePartitionWallAttributes(safeIndex, attrs);
    },
    [safeIndex, updatePartitionWallAttributes],
  );

  const {
    handleAttributeValueChange,
    handleDeleteAttribute,
    toggleInputField,
    handleAttributeSelection,
    getSortedAttributes,
  } = useAttributeActions(
    attributes,
    setAttributes,
    setAttributeValues,
    setShowInputFields,
    setAttrSelected,
    setAttrInputValue,
    pushAttributesToStore,
    allAttributes,
    setAllAttributes,
    setAttrError,
  );

  // Re-init local attribute UI state whenever the resolved wall changes
  useEffect(() => {
    const freshAttributes = (wall?.attributes ?? []) as AttributeModel[];

    setAttributes(freshAttributes);

    setAttributeValues(
      freshAttributes.reduce((acc, a) => {
        if (a?.value) acc[a.id] = a.value;
        return acc;
      }, {} as Record<number, string>),
    );

    setShowInputFields(
      freshAttributes.reduce((acc, a) => {
        acc[a.id] = !!a?.value;
        return acc;
      }, {} as Record<number, boolean>),
    );
  }, [wallUuid, wall]);

  // Unit change for an attribute
  const handleAttributeUnitChange = useCallback(
    (attrId: number, unit: string) => {
      const updated = attributes.map((a) =>
        a.id === attrId
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              a.predecessorId,
              a.type,
              unit,
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );

      setAttributes(updated);
      if (safeIndex < 0) return;
      updatePartitionWallAttributes(safeIndex, updated);
    },
    [attributes, safeIndex, updatePartitionWallAttributes],
  );

  // ----------------------------
  // Fetch full attribute catalog
  // ----------------------------
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const catalog = await filesApiService.getAttributes(regionCode);
        setAllAttributes(catalog);
        setAttrError(null);
      } catch (e) {
        setAttrError(`Error fetching attributes: ${(e as Error).message}`);
      }
    })();
  }, [open, regionCode]);

  // ----------------------------
  // Dimensions UI State
  // ----------------------------
  const [wallLength, setWallLengthState] = useState<number>(0);
  const [inputWallThickness, setInputWallThickness] = useState<number>(0);

  // Sync dimensions when wall or unit changes
  useEffect(() => {
    if (!wall) return;

    const pw = wall as PartitionWallModel;

    if (unitType === EUnitType.METRIC) {
      const nextLen =
        pw?.sideLength !== null
          ? internalToCentimetersRounded(pw.sideLength * UNIT_TO_METER_DIVISOR)
          : MIN_WALL_LENGTH;

      const nextThk = wall.depth ? internalToCentimetersRounded(wall.depth) : MIN_WALL_DEPTH;

      setWallLengthState(nextLen);
      setInputWallThickness(nextThk);
    } else {
      const nextLen =
        pw?.sideLength !== null
          ? internalToInchesRounded(pw.sideLength * UNIT_TO_METER_DIVISOR)
          : MIN_WALL_LENGTH_IMPERIAL;

      const nextThk = wall.depth ? internalToInchesRounded(wall.depth) : MIN_WALL_DEPTH_IMPERIAL;

      setWallLengthState(nextLen);
      setInputWallThickness(nextThk);
    }
  }, [wallUuid, wall, unitType]);

  const validateAndSet = useCallback(
    (value: number, type: EWallInput) => {
      if (safeIndex < 0) return;

      let maxValue: number;
      let minValue: number;

      if (unitType === EUnitType.METRIC) {
        if (type === EWallInput.LENGTH) {
          minValue = MIN_WALL_LENGTH;
          maxValue = MAX_WALL_LENGTH;
        } else {
          minValue = MIN_WALL_DEPTH;
          maxValue = MAX_WALL_DEPTH;
        }
      } else {
        if (type === EWallInput.LENGTH) {
          minValue = MIN_WALL_LENGTH_IMPERIAL;
          maxValue = MAX_WALL_LENGTH_IMPERIAL;
        } else {
          minValue = MIN_WALL_DEPTH_IMPERIAL;
          maxValue = MAX_WALL_DEPTH_IMPERIAL;
        }
      }

      if (isNaN(value)) {
        // revert to current wall values (already in state)
        if (type === EWallInput.LENGTH) {
          // no-op
        } else {
          // no-op
        }
        return;
      }

      let clamped = value;
      if (clamped < minValue) clamped = minValue;
      if (clamped > maxValue) clamped = maxValue;

      const roundedInternalValue =
        unitType === EUnitType.METRIC ? cmToInternalRounded(clamped) : inchToInternalRounded(clamped);

      if (type === EWallInput.DEPTH) {
        setInputWallThickness(clamped);
        updatePartitionWallThickness(safeIndex, roundedInternalValue);
      } else {
        setWallLengthState(clamped);
        updatePartitionWallLength(safeIndex, roundedInternalValue);
      }
    },
    [
      safeIndex,
      unitType,
      updatePartitionWallThickness,
      updatePartitionWallLength,
      setInputWallThickness,
      setWallLengthState,
    ],
  );

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>, type: EWallInput) => {
    const newValue = parseInt(event.target.value, 10);
    if (isNaN(newValue)) return;

    if (type === EWallInput.LENGTH) setWallLengthState(newValue);
    else setInputWallThickness(newValue);
  }, []);

  const handleInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>, type: EWallInput) => {
      const value = parseInt(event.target.value, 10);
      validateAndSet(value, type);
    },
    [validateAndSet],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, type: EWallInput) => {
      const value = parseInt((event.target as HTMLInputElement).value, 10);

      if (event.key === 'Enter') {
        validateAndSet(value, type);
        return;
      }

      if (!isNaN(value)) {
        if (type === EWallInput.LENGTH) setWallLengthState(value);
        else setInputWallThickness(value);
      }
    },
    [validateAndSet],
  );

  const handleCloseDrawer = useCallback(() => {
    resetSelection();
    onClose();
  }, [resetSelection, onClose]);

  const handleOnDelete = useCallback(() => {
    if (safeIndex < 0) return;
    deletePartitionWall(safeIndex);
    resetSelection();
    onClose();
  }, [safeIndex, deletePartitionWall, resetSelection, onClose]);

  const lengthWheelConfig: WheelInputConfig = useMemo(
    () => ({
      unitType,
      minMetric: MIN_WALL_LENGTH,
      maxMetric: MAX_WALL_LENGTH,
      minInches: MIN_WALL_LENGTH_IMPERIAL,
      maxInches: MAX_WALL_LENGTH_IMPERIAL,
    }),
    [unitType],
  );

  const thicknessWheelConfig: WheelInputConfig = useMemo(
    () => ({
      unitType,
      minMetric: MIN_WALL_DEPTH,
      maxMetric: MAX_WALL_DEPTH,
      minInches: MIN_WALL_DEPTH_IMPERIAL,
      maxInches: MAX_WALL_DEPTH_IMPERIAL,
    }),
    [unitType],
  );

  // If selection is invalid, don't render anything
  if (!open) return null;
  if (!wall || safeIndex < 0) return null;

  return (
    <Drawer
      key={id}
      anchor="right"
      open={open}
      variant={open ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          top: '64px',
          zIndex: 1000,
          width: '420px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Grid container direction="column" p={0}>
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              <FormattedMessage id="PartitionWallDrawer.wall" />
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDrawer}
            sx={{
              color: '#fff',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>
      </Grid>

      <Grid container item direction="column" spacing={3} p={3}>
        {/* LENGTH */}
        <Grid item>
          <Typography
            variant="subtitle2"
            gutterBottom
            style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}
          >
            <FormattedMessage
              id="PartitionWallDrawer.wallLength"
              values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }}
            />
          </Typography>

          <TextField
            fullWidth
            type="number"
            value={wallLength}
            onChange={(e) => handleInputChange(e, EWallInput.LENGTH)}
            onBlur={(e) => handleInputBlur(e, EWallInput.LENGTH)}
            onKeyUp={(e) => handleKeyPress(e, EWallInput.LENGTH)}
            onWheel={(e) =>
              handleWheelInput(
                e,
                wallLength.toString(),
                (v: string) => {
                  const newVal = parseInt(v, 10);
                  if (!isNaN(newVal)) setWallLengthState(newVal);
                },
                lengthWheelConfig,
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SyncAltIcon />
                </InputAdornment>
              ),
              inputProps: {
                min: unitType === EUnitType.METRIC ? MIN_WALL_LENGTH : MIN_WALL_LENGTH_IMPERIAL,
                max: unitType === EUnitType.METRIC ? MAX_WALL_LENGTH : MAX_WALL_LENGTH_IMPERIAL,
              },
            }}
          />
        </Grid>

        {/* THICKNESS */}
        <Grid item>
          <Typography
            variant="subtitle2"
            gutterBottom
            style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}
          >
            <FormattedMessage
              id="PartitionWallDrawer.wallThickness"
              values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }}
            />
          </Typography>

          <TextField
            fullWidth
            type="number"
            value={inputWallThickness}
            onChange={(e) => handleInputChange(e, EWallInput.DEPTH)}
            onBlur={(e) => handleInputBlur(e, EWallInput.DEPTH)}
            onKeyUp={(e) => handleKeyPress(e, EWallInput.DEPTH)}
            onWheel={(e) =>
              handleWheelInput(
                e,
                inputWallThickness.toString(),
                (v: string) => {
                  const newVal = parseInt(v, 10);
                  if (!isNaN(newVal)) setInputWallThickness(newVal);
                },
                thicknessWheelConfig,
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LineWeightIcon />
                </InputAdornment>
              ),
              inputProps: {
                min: unitType === EUnitType.METRIC ? MIN_WALL_DEPTH : MIN_WALL_DEPTH_IMPERIAL,
                max: unitType === EUnitType.METRIC ? MAX_WALL_DEPTH : MAX_WALL_DEPTH_IMPERIAL,
              },
            }}
          />
        </Grid>

        {/* ATTRIBUTES */}
        <Grid item>
          <AttributeList
            attributes={getSortedAttributes(attributes)}
            attributeValues={attributeValues}
            showInputFields={showInputFields}
            onAttributeValueChange={handleAttributeValueChange}
            onAttributeUnitChange={handleAttributeUnitChange}
            onDeleteAttribute={handleDeleteAttribute}
            onToggleInputField={toggleInputField}
            onReorderAttributes={(newAttrs) => {
              setAttributes(newAttrs);
              if (safeIndex < 0) return;
              updatePartitionWallAttributes(safeIndex, newAttrs);
            }}
          />
        </Grid>

        <Grid item>
          <Box sx={{ mt: -5, mb: 1, width: '99%', mx: 'auto' }}>
            <AttributeAutocomplete
              attributes={allAttributes}
              disabledAttributeIds={attributes.map((attribute) => attribute.id)}
              selected={attrSelected}
              inputValue={attrInputValue}
              error={attrError}
              onChange={handleAttributeSelection}
              onInputChange={(val) => {
                setAttrInputValue(val);
                if (val !== attrSelected?.key) setAttrSelected(null);
              }}
              placeholder={intl.formatMessage({
                id: 'AttributeAutocomplete.addAttribute',
                defaultMessage: 'Add attribute',
              })}
            />
          </Box>
        </Grid>

        {/* DELETE */}
        <Grid item>
          <Button
            variant="contained"
            startIcon={<DeleteIcon />}
            onClick={handleOnDelete}
            sx={{ backgroundColor: deleteButton }}
          >
            <FormattedMessage id="PartitionWallDrawer.deleteWall" />
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default PartitionWallDrawer;
